// 主页 (v1.4 / Day-10)
//
// 重大改动：
//   - 顶栏：左 ☰ + 中 (alias + 双 PC 圆点 + ws-state) + 右 ⏻
//   - 底部：去掉双胶囊（已上移到顶栏），项目条压成 28px mini bar
//   - ConnectionBanner: position absolute 防 CLS（V2-2）
//   - V2-7 keyboard inset + 100dvh
//   - V2-11 textarea 改非受控 ref 模式防 IME 吞字
//   - Slice C 起会再加 Markdown / ToolCallCard 渲染

import { useEffect, useRef, useState } from "preact/hooks";
import { effect } from "@preact/signals";
import {
  agents,
  agentsError,
  agentsLastFetch,
  agentsLoading,
  startAgentsPolling,
  stopAgentsPolling
} from "../state/agents";
import {
  chat,
  handleEnvelope,
  pushChat,
  pushLog,
  selectedAgentId,
  selectedCwd,
  selectedResumeId,
  continueRecent,
  currentClaudeSessionId,
  streamingSessionId,
  killPending,
  wsLog,
  wsState,
  wsStateDetail,
} from "../state/session";
import { WSClient } from "../transport/ws";
import { CliRPC } from "../transport/cli";
import { tokenStore } from "../storage/tokens";
import {
  getSubscriptionState,
  subscribePush,
  triggerPushTest,
  unsubscribePush,
} from "../transport/push";
import { ProjectPicker } from "../components/ProjectPicker";
import { ConnectionBanner } from "../components/ConnectionBanner";
import { Markdown } from "../components/Markdown";

let wsClient: WSClient | null = null;
let cliRpc: CliRPC | null = null;

function ensureWS() {
  if (wsClient) return wsClient;
  wsClient = new WSClient({
    onState: (s, d) => {
      wsState.value = s;
      wsStateDetail.value = d ?? "";
    },
    onLog: (line) => pushLog(line),
    onMessage: (env) => {
      // Day-8: 先让 CliRPC 拦截 ack_id 匹配的回复
      if (cliRpc?.handleEnvelope(env)) return;
      handleEnvelope(env);
    }
  });
  cliRpc = new CliRPC(wsClient);
  wsClient.start();
  return wsClient;
}

export function HomePage({ onLogout }: { onLogout: () => void }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [identityAlias, setIdentityAlias] = useState("📱");
  const [pushPerm, setPushPerm] = useState<NotificationPermission>("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState<string | null>(null);

  // V2-11: textarea 用 ref 非受控，防 IME 吞字
  const taRef = useRef<HTMLTextAreaElement>(null);
  // sendEnabled 用 state 控制按钮，仅靠 input 事件触发
  const [sendEnabled, setSendEnabled] = useState(false);

  function autosize() {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  }

  function recomputeSendEnabled() {
    const t = (taRef.current?.value ?? "").trim();
    setSendEnabled(t.length > 0 && wsState.value === "open" && !!selectedAgentId.value && !streamingSessionId.value);
  }

  async function refreshPushState() {
    const s = await getSubscriptionState();
    setPushPerm(s.permission);
    setPushSubscribed(s.subscribed);
  }

  async function onTogglePush() {
    setPushBusy(true);
    setPushMsg(null);
    try {
      if (pushSubscribed) {
        await unsubscribePush();
        setPushMsg("已取消订阅");
      } else {
        const r = await subscribePush();
        setPushMsg(r.ok ? "已订阅 ✓" : `失败：${r.reason}`);
      }
    } catch (e) {
      setPushMsg(`错误：${(e as Error).message}`);
    } finally {
      setPushBusy(false);
      void refreshPushState();
    }
  }

  async function onPushTest() {
    setPushBusy(true);
    setPushMsg(null);
    try {
      await triggerPushTest();
      setPushMsg("已触发测试推送（Bark + Web Push）");
    } catch (e) {
      setPushMsg(`错误：${(e as Error).message}`);
    } finally {
      setPushBusy(false);
    }
  }

  useEffect(() => {
    void (async () => {
      const id = await tokenStore.getIdentity();
      if (id) setIdentityAlias(id.alias);
    })();
    void refreshPushState();
    startAgentsPolling(5000);
    ensureWS();
    return () => {
      stopAgentsPolling();
      wsClient?.stop();
      wsClient = null;
    };
  }, []);

  // 默认选第一台在线 agent，并刷新 sendEnabled 当 selectedAgentId / wsState / streaming 变化
  useEffect(() => {
    return effect(() => {
      // tap signals
      void selectedAgentId.value;
      void wsState.value;
      void streamingSessionId.value;
      if (!selectedAgentId.value) {
        const first = agents.value.find((a) => a.online) ?? agents.value[0];
        if (first) selectedAgentId.value = first.id;
      }
      recomputeSendEnabled();
    });
  }, []);

  function onSubmit(e?: Event) {
    e?.preventDefault();
    const ta = taRef.current;
    if (!ta) return;
    const text = ta.value.trim();
    if (!text) return;
    if (!selectedAgentId.value) {
      pushChat({ role: "system", text: "未选中 PC", ts: Date.now() });
      return;
    }
    if (wsState.value !== "open") {
      pushChat({ role: "system", text: `WS 未连接（${wsState.value}）`, ts: Date.now() });
      return;
    }
    const sessionId = `session-${selectedAgentId.value}-${Date.now()}`;
    pushChat({ role: "user", text, ts: Date.now() });

    // Day-9 续接优先级：picker 选的 > continue_recent > 自动接当前对话 > 全新
    let resumeSessionId = selectedResumeId.value || "";
    let continueRecentFlag = continueRecent.value;
    if (!resumeSessionId && !continueRecentFlag && currentClaudeSessionId.value) {
      resumeSessionId = currentClaudeSessionId.value;
    }

    streamingSessionId.value = sessionId;
    killPending.value = false;

    wsClient?.send("send_command", {
      target_agent_id: selectedAgentId.value,
      session_id: sessionId,
      content: text,
      cwd: selectedCwd.value || "",
      resume_session_id: resumeSessionId,
      continue_recent: continueRecentFlag,
    });
    // 清空 textarea + 高度
    ta.value = "";
    ta.style.height = "auto";
    setSendEnabled(false);
  }

  function onStop() {
    if (!streamingSessionId.value || !selectedAgentId.value) return;
    killPending.value = true;
    wsClient?.send("cmd_kill_cc", {
      target_agent_id: selectedAgentId.value,
      session_id: streamingSessionId.value,
      reason: "user_cancel",
    });
    pushChat({
      role: "system",
      text: "→ 已请求中止 (CTRL+BREAK)…",
      ts: Date.now(),
    });
  }

  function onResetConversation() {
    selectedResumeId.value = "";
    continueRecent.value = false;
    currentClaudeSessionId.value = "";
  }

  // 当前续接策略简短描述
  const resumeLabel = selectedResumeId.value
    ? `续接 ${selectedResumeId.value.slice(0, 8)}`
    : continueRecent.value
    ? "续接最近一次"
    : currentClaudeSessionId.value
    ? `对话中 ${currentClaudeSessionId.value.slice(0, 8)}`
    : "新对话";

  const inMultiTurn = !!currentClaudeSessionId.value
    && !selectedResumeId.value
    && !continueRecent.value;

  function cwdShort(): string {
    const c = selectedCwd.value;
    if (!c) return "未选择目录";
    const parts = c.split(/[\\\/]+/).filter(Boolean);
    return parts.length > 2 ? "…" + parts.slice(-2).join("/") : c;
  }

  return (
    <div class="page page-home">
      {/* 顶栏：左 ☰ + 中（alias / 双 PC 圆点 / ws 小字）+ 右 ⏻ */}
      <header class="topbar">
        <button
          class="icon-btn"
          aria-label="侧栏"
          onClick={() => setDrawerOpen(true)}
        >
          ☰
        </button>
        <div class="topbar__title">
          <div class="topbar__alias">
            <span>{identityAlias}</span>
            <div class="topbar__pcs">
              {agents.value.length === 0 && agentsLoading.value && (
                <span class="muted small">…</span>
              )}
              {agents.value.map((a) => {
                const sel = selectedAgentId.value === a.id;
                const cls = [
                  "pc-dot",
                  sel ? "pc-dot--selected" : "",
                  a.online ? "pc-dot--online" : "pc-dot--offline",
                ].join(" ");
                return (
                  <button
                    key={a.id}
                    class={cls}
                    title={`${a.alias} (${a.id}) · ${a.online ? "在线" : "离线"}`}
                    onClick={() => (selectedAgentId.value = a.id)}
                  >
                    {/* 取 alias 第一个 emoji 字符（🏠/🏢） */}
                    {Array.from(a.alias)[0] ?? "?"}
                  </button>
                );
              })}
            </div>
          </div>
          <span class="topbar__ws-state" data-state={wsState.value}>
            {selectedAgentId.value ? `→ ${selectedAgentId.value}` : "未选 PC"}
          </span>
        </div>
        <button class="icon-btn" aria-label="退出" onClick={onLogout}>
          ⏻
        </button>
      </header>

      {/* V2-2 ConnectionBanner: 顶栏下方悬浮 */}
      <ConnectionBanner onRetry={() => wsClient?.reconnect()} />

      {/* 聊天流 */}
      <main class="chat">
        {chat.value.length === 0 && (
          <div class="empty">
            <p>选 PC，输入命令</p>
            <p class="small">Slice A+B：视觉重写 + Markdown 渲染 / 代码块复制已上线</p>
            <p class="small">Slice C 即将到位：工具调用可视化（Bash / Edit / Read 卡片）</p>
          </div>
        )}
        {chat.value.map((m, i) => (
          <div class={`msg msg-${m.role}`} key={i}>
            <div class={`msg__bubble ${m.streaming ? "is-streaming" : ""}`}>
              {m.role === "assistant" ? (
                // Slice B: assistant 走 Markdown 渲染（含流式 rAF 节流 + ErrorBoundary 兜底）
                <Markdown text={m.text} streaming={!!m.streaming} />
              ) : (
                // user / system 保持纯文本（用户自己输入的不渲染 markdown，防自己手抖输入 ``` 引起样式抖）
                m.text
              )}
            </div>
          </div>
        ))}
        {agentsError.value && (
          <div class="msg msg-system">
            <div class="msg__bubble">⚠ /api/agents 拉取失败：{agentsError.value}</div>
          </div>
        )}
        {/* WS log 调试条 */}
        <details class="ws-log">
          <summary>WS log ({wsLog.value.length})</summary>
          <pre>{wsLog.value.join("\n")}</pre>
        </details>
      </main>

      {/* 底部：项目条 mini bar + 输入区 */}
      <footer class="bottom">
        <div class="project-bar">
          <button
            type="button"
            class="project-bar__btn"
            onClick={() => setPickerOpen(true)}
            disabled={!selectedAgentId.value || wsState.value !== "open"}
            title="选项目目录 + 续接策略"
          >
            <span class="project-bar__icon">📁</span>
            <span class="project-bar__cwd">{cwdShort()}</span>
            <span class={`project-bar__resume ${
              (selectedResumeId.value || continueRecent.value || inMultiTurn)
                ? "project-bar__resume--active"
                : ""
            }`}>
              {resumeLabel}
            </span>
            <span class="project-bar__arrow">▾</span>
          </button>
          {(selectedResumeId.value || continueRecent.value || currentClaudeSessionId.value) && (
            <button
              type="button"
              class="project-bar__reset"
              onClick={onResetConversation}
              title="清空续接状态，下一条消息开新对话"
            >
              ↻
            </button>
          )}
        </div>

        <form class="composer" onSubmit={onSubmit}>
          {/* V2-11: 非受控 textarea，使用 ref 避免 IME 吞字 */}
          <textarea
            ref={taRef}
            class="composer__input"
            rows={1}
            placeholder={
              streamingSessionId.value
                ? "claude 正在跑…（点 ⏹ 中止）"
                : selectedAgentId.value
                ? "消息 Claude…"
                : "选一台 PC"
            }
            disabled={
              !selectedAgentId.value
              || wsState.value !== "open"
              || !!streamingSessionId.value
            }
            onInput={() => { autosize(); recomputeSendEnabled(); }}
            onKeyDown={(e) => {
              // V2-11: 中文 IME 组合态时不发（Preact 的 KeyboardEvent 直接就是 DOM 原生 event）
              if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
                e.preventDefault();
                onSubmit(e as unknown as Event);
              }
            }}
          />
          {streamingSessionId.value ? (
            <button
              type="button"
              class="composer__stop"
              onClick={onStop}
              disabled={killPending.value}
              title="发 CTRL+BREAK 优雅中止"
            >
              {killPending.value ? "…" : "⏹"}
            </button>
          ) : (
            <button
              class="composer__send"
              type="submit"
              disabled={!sendEnabled}
            >
              ↑
            </button>
          )}
        </form>

        <div class="status-line">
          {agentsLastFetch.value > 0 &&
            `last update ${new Date(agentsLastFetch.value * 1000).toLocaleTimeString()}`}
        </div>
      </footer>

      {pickerOpen && (
        <ProjectPicker rpc={cliRpc} onClose={() => setPickerOpen(false)} />
      )}

      {drawerOpen && (
        <div class="drawer-mask" onClick={() => setDrawerOpen(false)}>
          <aside class="drawer" onClick={(e) => e.stopPropagation()}>
            <h2>设置</h2>

            <section class="drawer-section">
              <h3>通知推送</h3>
              <p class="muted small">
                授权后，PC 离线 / 任务完成时会推到 iPhone 锁屏。
                Bark 走 https://api.day.app；Web Push 走 VAPID。
              </p>
              <div class="push-state">
                <span>权限：<b>{pushPerm}</b></span>
                <span>订阅：<b>{pushSubscribed ? "✓" : "—"}</b></span>
              </div>
              <div class="row">
                <button
                  class="btn btn-primary"
                  disabled={pushBusy}
                  onClick={onTogglePush}
                >
                  {pushSubscribed ? "取消订阅" : "授权 + 订阅"}
                </button>
                <button class="btn" disabled={pushBusy} onClick={onPushTest}>
                  发测试
                </button>
              </div>
              {pushMsg && <div class="muted small">{pushMsg}</div>}
            </section>

            <section class="drawer-section">
              <h3>会话历史</h3>
              <p class="muted small">Day-11 起从 Agent 拉历史 (P-3 协议层)</p>
            </section>

            <button onClick={onLogout} class="btn btn-danger">退出登录</button>
          </aside>
        </div>
      )}
    </div>
  );
}
